import type { SBotAppRoute } from "./appNavigation";

export const DEFAULT_NAVIGATION_CAPABILITY_TTL_MS = 8 * 60 * 60 * 1_000;

export interface SBotNavigationCapabilitySession {
  userID: string;
  conversationID: string;
  workspaceStorageKey?: string | undefined;
}

export interface SBotNavigationCapabilityRegistration {
  handle: string;
  title: string;
  breadcrumb: string;
  route: SBotAppRoute;
  sourceTool: string;
}

export interface SBotNavigationCapabilityRecord extends SBotNavigationCapabilityRegistration {
  userID: string;
  conversationID: string;
  workspaceStorageKey?: string | undefined;
  discoveredAt: Date;
  expiresAt: Date;
}

export type SBotNavigationCapabilityErrorKind =
  | "noActiveConversation"
  | "wrongUser"
  | "wrongConversation"
  | "wrongWorkspace"
  | "undiscoveredHandle"
  | "expiredHandle";

const CAPABILITY_ERROR_MESSAGES: Record<SBotNavigationCapabilityErrorKind, string> = {
  noActiveConversation: "App navigation discovery is not attached to an active conversation.",
  wrongUser: "The discovered app handle belongs to a different signed-in user.",
  wrongConversation: "The app handle was not discovered in the active conversation.",
  wrongWorkspace: "The discovered app handle belongs to a different workspace.",
  undiscoveredHandle: "The app handle has not been discovered in the active conversation.",
  expiredHandle: "The discovered app handle has expired. Discover the app view again.",
};

export class SBotNavigationCapabilityError extends Error {
  readonly kind: SBotNavigationCapabilityErrorKind;

  constructor(kind: SBotNavigationCapabilityErrorKind) {
    super(CAPABILITY_ERROR_MESSAGES[kind]);
    this.name = "SBotNavigationCapabilityError";
    this.kind = kind;
  }
}

interface ActiveSession extends SBotNavigationCapabilitySession {
  id: string;
}

interface StoredRecord extends SBotNavigationCapabilityRecord {
  sessionID: string;
}

/**
 * Conversation-scoped, in-memory proof that an app destination was discovered.
 *
 * `beginSession` always rotates the opaque session namespace. Presentation
 * changes between full-screen and floating chat must reuse this same store and
 * must not call `beginSession`.
 */
export class SBotNavigationCapabilityStore {
  private activeSession: ActiveSession | undefined;
  private readonly recordsByHandle = new Map<string, StoredRecord>();

  constructor(
    private readonly now: () => Date = () => new Date(),
    private readonly ttlMS: number = DEFAULT_NAVIGATION_CAPABILITY_TTL_MS,
  ) {
    if (!Number.isFinite(ttlMS) || ttlMS <= 0) {
      throw new RangeError("Navigation capability TTL must be a positive finite duration.");
    }
  }

  beginSession(session: SBotNavigationCapabilitySession): void {
    const userID = session.userID.trim();
    const conversationID = session.conversationID.trim();
    if (!userID || !conversationID) {
      throw new SBotNavigationCapabilityError("noActiveConversation");
    }
    this.recordsByHandle.clear();
    this.activeSession = {
      userID,
      conversationID,
      workspaceStorageKey: session.workspaceStorageKey,
      id: crypto.randomUUID(),
    };
  }

  clearSession(): void {
    this.activeSession = undefined;
    this.recordsByHandle.clear();
  }

  register(registration: SBotNavigationCapabilityRegistration): SBotNavigationCapabilityRecord {
    const session = this.requireActiveSession();
    const timestamp = this.now();
    this.removeExpiredRecords(timestamp);
    const stored: StoredRecord = {
      ...registration,
      route: { ...registration.route },
      userID: session.userID,
      conversationID: session.conversationID,
      workspaceStorageKey: session.workspaceStorageKey,
      discoveredAt: timestamp,
      expiresAt: new Date(timestamp.getTime() + this.ttlMS),
      sessionID: session.id,
    };
    this.recordsByHandle.set(registration.handle, stored);
    return publicRecord(stored);
  }

  record(
    handle: string,
    context: SBotNavigationCapabilitySession,
  ): SBotNavigationCapabilityRecord {
    const session = this.requireActiveSession();
    if (session.userID !== context.userID) {
      throw new SBotNavigationCapabilityError("wrongUser");
    }
    if (session.conversationID !== context.conversationID) {
      throw new SBotNavigationCapabilityError("wrongConversation");
    }
    if (session.workspaceStorageKey !== context.workspaceStorageKey) {
      throw new SBotNavigationCapabilityError("wrongWorkspace");
    }

    const stored = this.recordsByHandle.get(handle);
    if (!stored) throw new SBotNavigationCapabilityError("undiscoveredHandle");
    if (stored.sessionID !== session.id || stored.conversationID !== context.conversationID) {
      throw new SBotNavigationCapabilityError("wrongConversation");
    }
    if (stored.userID !== context.userID) {
      throw new SBotNavigationCapabilityError("wrongUser");
    }
    if (stored.workspaceStorageKey !== context.workspaceStorageKey) {
      throw new SBotNavigationCapabilityError("wrongWorkspace");
    }
    if (stored.expiresAt.getTime() <= this.now().getTime()) {
      this.recordsByHandle.delete(handle);
      throw new SBotNavigationCapabilityError("expiredHandle");
    }
    return publicRecord(stored);
  }

  get recordCount(): number {
    this.removeExpiredRecords(this.now());
    return this.recordsByHandle.size;
  }

  private requireActiveSession(): ActiveSession {
    if (!this.activeSession) {
      throw new SBotNavigationCapabilityError("noActiveConversation");
    }
    return this.activeSession;
  }

  private removeExpiredRecords(timestamp: Date): void {
    for (const [handle, record] of this.recordsByHandle) {
      if (record.expiresAt.getTime() <= timestamp.getTime()) {
        this.recordsByHandle.delete(handle);
      }
    }
  }
}

function publicRecord(record: StoredRecord): SBotNavigationCapabilityRecord {
  const { sessionID: _sessionID, ...value } = record;
  return {
    ...value,
    route: { ...value.route },
    discoveredAt: new Date(value.discoveredAt),
    expiresAt: new Date(value.expiresAt),
  };
}
